<template>
  <div
    class="product__gallery product__gallery--ab-variant"
    :class="{ 'initial-image-png': hasColouredBgOption }"
  >
    <div class="product__gallery-inner product__gallery-inner--ab-variant">
      <ProductSaleBadge />

      <div class="product__gallery-layout product__gallery-layout--ab-variant">
        <aside class="product__gallery-aside product__gallery-aside--ab-variant">
          <div
            v-for="(block, index) in heroHighlightBlocks"
            :key="`highlight-${index}`"
            class="product__gallery-aside-card"
          >
            <p class="product__gallery-aside-title">
              {{ block.title }}
            </p>
            <p class="product__gallery-aside-subtitle">
              {{ block.subtitle }}
            </p>
          </div>
        </aside>

        <div class="product__gallery-main-container--ab-variant">
          <div class="product__gallery-main-slider-wrap">
            <vue-flickity
              :key="`main-${product.id}-${abReadyKey}`"
              ref="mainCarousel"
              class="product__gallery-main-image-slider product__gallery-main-image-slider--ab-variant"
              :options="{
                prevNextButtons: false,
                pageDots: true,
                lazyLoad: 2,
                cellAlign: 'left',
                wrapAround: true,
              }"
            >
              <div
                v-for="(image, index) in largeMedia"
                :key="`main-image-${index}`"
                class="product__gallery-main-image"
                :data-elite-points="index === 0 ? pointsText : null"
                :data-promo-badge="index === 0 ? promoBadge : null"
              >
                <template v-if="index === 0">
                  <img
                    v-if="image.media_type === 'image'"
                    :src="image.preview_image.src"
                    :alt="`image-${index}`"
                    width="685"
                    height="685"
                    loading="eager"
                    class="hidden md:block"
                  >
                  <img
                    v-if="image.media_type === 'image'"
                    :src="image.preview_image.mobileSrc || image.preview_image.src"
                    :alt="`image-${index}`"
                    width="288"
                    height="288"
                    loading="eager"
                    class="md:hidden"
                  >
                </template>
                <template v-else>
                  <img
                    v-if="image.media_type === 'image'"
                    :data-flickity-lazyload="image.preview_image.src"
                    :alt="`image-${index}`"
                    width="685"
                    height="685"
                    class="hidden md:block"
                  >
                  <img
                    v-if="image.media_type === 'image'"
                    :src="image.preview_image.mobileSrc || image.preview_image.src"
                    :alt="`image-${index}`"
                    width="288"
                    height="288"
                    class="md:hidden"
                    loading="lazy"
                  >
                </template>

                <video
                  v-if="image.media_type === 'video'"
                  width="685"
                  height="685"
                  controls
                >
                  <source
                    v-for="source in image.sources"
                    :key="source.format"
                    :src="source.url"
                    :type="source.mime_type"
                  >
                  Your browser does not support the video tag.
                </video>
              </div>
            </vue-flickity>

            <!-- Mobile-only highlight blocks inside the slider wrap -->
            <aside class="product__gallery-aside product__gallery-aside--mobile">
              <div
                v-for="(block, index) in heroHighlightBlocks"
                :key="`highlight-mobile-${index}`"
                class="product__gallery-aside-card"
              >
                <p class="product__gallery-aside-title">
                  {{ block.title }}
                </p>
                <p class="product__gallery-aside-subtitle">
                  {{ block.subtitle }}
                </p>
              </div>
            </aside>
          </div>

          <div class="product__gallery-thumbnails product__gallery-thumbnails--ab-variant">
            <button
              v-if="showThumbPrev"
              id="product-gallery-thumb-prev"
              class="product__gallery-button product__gallery-button--prev product__gallery-button--ab-variant"
              :class="{ 'product__gallery-button--disabled': atFirstSlide }"
              :disabled="atFirstSlide"
              aria-label="Previous Slide"
              type="button"
              @click="previousSlide"
            >
              <ArrowLeft />
            </button>

            <vue-flickity
              :key="`thumbs-${product.id}-${abReadyKey}`"
              ref="thumbnailCarousel"
              class="product__gallery-thumbnails-inner product__gallery-thumbnails--ab-variant-flickity"
              :options="{
                prevNextButtons: false,
                pageDots: false,
                setGallerySize: false,
                contain: true,
                cellSelector: '.product__gallery-thumbnail-wrapper',
                cellAlign: 'left',
                wrapAround: true,
                draggable: true,
                selectedAttraction: 0
              }"
            >
              <div
                v-for="(image, index) in thumbnailImages"
                :key="`thumbnail-${index}`"
                class="product__gallery-thumbnail-wrapper product__gallery-thumbnail-wrapper--ab-variant"
                :class="{ 'product__gallery-thumbnail-wrapper--active': currentSlideIndex === index }"
              >
                <button
                  type="button"
                  class="product__gallery-thumbnail product__gallery-thumbnail--ab-variant"
                  :class="{
                    'product__gallery-thumbnail--png': image.includes('.png'),
                    'product__gallery-thumbnail--active': currentSlideIndex === index
                  }"
                  @click.stop="goToSlide(index)"
                >
                  <img
                    :src="image"
                    :alt="`thumbnail-image-${index}`"
                    width="60"
                    height="60"
                  >
                </button>
              </div>
            </vue-flickity>

            <button
              v-if="showThumbNext"
              id="product-gallery-thumb-next"
              class="product__gallery-button product__gallery-button--next product__gallery-button--ab-variant"
              :class="{ 'product__gallery-button--disabled': atLastSlide }"
              :disabled="atLastSlide"
              aria-label="Next Slide"
              type="button"
              @click="nextSlide"
            >
              <ArrowLeft />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useProductStore } from '../../pinia/ProductStore'
import { productPointsValue } from '../../helpers/product'

import ProductSaleBadge from './product-sale-badge'
import ArrowLeft from '../../icons/arrow-left'

export default {
  name: 'ProductGalleryBottomThumbs',
  components: {
    ArrowLeft,
    ProductSaleBadge
  },
  data () {
    return {
      heroHighlightBlocks: [
        {
          title: '150mg',
          subtitle: 'Caffeine'
        },
        {
          title: 'Zero',
          subtitle: 'Sugar'
        },
        {
          title: '22+',
          subtitle: 'Flavours'
        }
      ],
      isMobileViewport: false,
      thumbnailNavThreshold: 6,
      currentSlideIndex: 0,
      atFirstSlide: true,
      atLastSlide: false,
      abReadyKey: 0
    }
  },
  computed: {
    ...mapState(useProductStore, [
      'product',
      'hasColouredBgOption',
      'thumbnailImages',
      'largeMedia'
    ]),
    gallerySize () {
      return this.largeMedia.length
    },
    promoBadge () {
      const badgeTag = this.product.tags.find(tag => tag.includes('promo-badge:'))
      if (badgeTag) {
        return badgeTag.split(':')[1]
      }
      return null
    },
    pointsText () {
      return productPointsValue(this.product.price)
    },
    shouldShowThumbnailNav () {
      return this.thumbnailImages.length > this.thumbnailNavThreshold
    },
    showThumbPrev () {
      return !this.isMobileViewport && this.shouldShowThumbnailNav
    },
    showThumbNext () {
      return !this.isMobileViewport && this.shouldShowThumbnailNav
    }
  },
  watch: {
    product () {
      this.resetGalleryState()
      this.$nextTick(() => {
        this.galleryLoad()
      })
    },
    thumbnailImages () {
      this.$nextTick(() => {
        this.galleryLoad()
      })
    }
  },
  mounted () {
    this.clearLoadingState()
    this.galleryLoad()
    if (typeof window !== 'undefined') {
      this.updateResponsiveState()
      window.addEventListener('resize', this.updateResponsiveState, { passive: true })
      window.addEventListener('orientationchange', this.updateResponsiveState, { passive: true })
      window.addEventListener('ab-ready', this.onAbReady)
    }
  },
  beforeUnmount () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateResponsiveState)
      window.removeEventListener('orientationchange', this.updateResponsiveState)
      window.removeEventListener('ab-ready', this.onAbReady)
    }
  },
  methods: {
    galleryLoad () {
      if (this.$el && this.$el.classList) {
        this.$el.classList.remove('product__gallery--loading')
      }
      if (this.$refs.thumbnailCarousel) {
        this.$refs.thumbnailCarousel.reloadCells()
      }
      this.galleryDragWatcher()
    },
    refreshSliderPositions (newSlideIndex) {
      this.atFirstSlide = newSlideIndex === 0
      this.atLastSlide = newSlideIndex === (this.gallerySize - 1)
      this.currentSlideIndex = newSlideIndex
    },
    galleryDragWatcher () {
      if (!this.$refs.mainCarousel) return
      this.$refs.mainCarousel.on('change', newSlideIndex => {
        if (this.$refs.thumbnailCarousel) {
          this.$refs.thumbnailCarousel.select(newSlideIndex)
          this.$refs.thumbnailCarousel.reloadCells()
        }
        this.refreshSliderPositions(newSlideIndex)
      })
    },
    previousSlide () {
      if (this.$refs.thumbnailCarousel) this.$refs.thumbnailCarousel.previous()
      if (this.$refs.mainCarousel) this.$refs.mainCarousel.previous()
    },
    nextSlide () {
      if (this.$refs.thumbnailCarousel) this.$refs.thumbnailCarousel.next()
      if (this.$refs.mainCarousel) this.$refs.mainCarousel.next()
    },
    goToSlide (index) {
      if (this.$refs.thumbnailCarousel) {
        this.$refs.thumbnailCarousel.select(index)
        this.$refs.thumbnailCarousel.reloadCells()
      }
      if (this.$refs.mainCarousel) {
        this.$refs.mainCarousel.select(index)
        this.$refs.mainCarousel.reloadCells()
      }
      this.refreshSliderPositions(index)
    },
    updateResponsiveState () {
      if (typeof window === 'undefined') {
        return
      }
      const isMobile = window.matchMedia('(max-width: 768px)').matches
      this.isMobileViewport = isMobile
      this.thumbnailNavThreshold = isMobile ? 4 : 6
    },
    resetGalleryState () {
      this.currentSlideIndex = 0
      this.atFirstSlide = true
      this.atLastSlide = false

      this.$nextTick(() => {
        if (this.$refs.thumbnailCarousel) {
          this.$refs.thumbnailCarousel.select(0)
        }
        if (this.$refs.mainCarousel) {
          this.$refs.mainCarousel.select(0)
        }
      })
    },
    onAbReady () {
      this.abReadyKey += 1
      this.$nextTick(() => {
        this.galleryLoad()
        this.clearLoadingState()
      })
    },
    clearLoadingState () {
      if (this.$el && this.$el.classList && this.$el.classList.contains('product__gallery--loading')) {
        this.$el.classList.remove('product__gallery--loading')
      }
    }
  }
}
</script>

